import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTapToggle } from '@/pages/sale/hooks/use-tap-toggle';
import { SIDEBAR_TABS, SaleTabKey } from '@/pages/sale/lib/sale-tab-config';
import { ChevronDown } from 'lucide-react';
import { TabBadge } from './TabBadge';

type SaleSidebarTabNavProps = {
  activeTab: SaleTabKey;
  tabCounters: Partial<Record<SaleTabKey, number>>;
  onTabChange: (tabKey: SaleTabKey) => void;
};

function TabLabel({ label, count, active }: { label: string; count: number; active: boolean }) {
  return (
    <>
      {label}
      {count > 0 && <TabBadge count={count} active={active} />}
    </>
  );
}

export function SaleSidebarDesktopTabNav({ activeTab, tabCounters, onTabChange }: SaleSidebarTabNavProps) {
  return (
    <div className="hidden md:flex shrink-0">
      {SIDEBAR_TABS.map((tab, index) => {
        const count = tabCounters[tab.key] ?? 0;
        const isActive = activeTab === tab.key;

        return (
          <Button
            key={tab.key}
            variant={isActive ? 'default' : 'outline'}
            onClick={() => onTabChange(tab.key)}
            className={`flex-1 rounded-none text-xs md:text-sm px-1 md:px-2 py-1.5 md:py-2${index < SIDEBAR_TABS.length - 1 ? ' border-r' : ''}`}
          >
            <TabLabel label={tab.label} count={count} active={isActive} />
          </Button>
        );
      })}
    </div>
  );
}

export function SaleSidebarMobileTabNav({ activeTab, tabCounters, onTabChange }: SaleSidebarTabNavProps) {
  const { open, setOpen, triggerProps } = useTapToggle();
  const activeTabConfig = SIDEBAR_TABS.find((tab) => tab.key === activeTab);
  const activeTabCount = tabCounters[activeTab] ?? 0;

  return (
    <div className="flex md:hidden shrink-0 w-full p-2 border-b">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full justify-between" {...triggerProps}>
            <span className="flex items-center gap-2">
              {activeTabConfig?.label}
              {activeTabCount > 0 && <TabBadge count={activeTabCount} active />}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
          {SIDEBAR_TABS.map((tab) => {
            const count = tabCounters[tab.key] ?? 0;
            const isActive = activeTab === tab.key;

            return (
              <DropdownMenuItem
                key={tab.key}
                onSelect={() => onTabChange(tab.key)}
                className="justify-between"
              >
                <span>{tab.label}</span>
                {count > 0 && <TabBadge count={count} active={isActive} />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
